import { useInfiniteQuery } from "@tanstack/react-query";
import log from "logbeacon";
import { OpenDataTypeEnum } from "@/src/features/api/openData";
import { getOpenDataApi } from "@/src/services/helper";
import {
  GetRankingThemesParams,
  ThemesPagination,
  ThemesQueryConfig,
  buildThemesQueryParams,
  buildThemesQueryKey,
} from "@/src/services/themesSSRService";
import { I18nUtil, Locale } from "@/src/utils";
import {
  ThemesScopeEnum,
  ThemesSortFieldEnum,
  StockMarketType,
} from "@/src/const";

interface UseThemesInfiniteQueryParams {
  type: OpenDataTypeEnum;
  sortField?: ThemesSortFieldEnum;
  enabled?: boolean;
  limit?: number; // 每页数量，默认 20
  sortOrder?: "DESC" | "ASC"; // 排序顺序，默认 DESC
  enrichWithMarket?: boolean; // 是否包含市场数据，默认 false
  enrichWithSymbol?: boolean; // 是否包含标的数据，默认 false
  enrichWithSymbolLimit?: number; // 标的数量限制，默认 5
  enrichEnable?: boolean; // 是否启用丰富化，默认 false
  stockType?: StockMarketType; // 股票市场类型（US/HK），仅当 type 为 STOCK 时使用
  /** 主题 scope（如 HeatMap / HotSearch / Recommend），可选 */
  scope?: ThemesScopeEnum;
}

/**
 * 客户端无限滚动加载主题数据
 * @param params 查询参数
 * @returns useInfiniteQuery 结果
 */
export const useThemesInfiniteQuery = ({
  type,
  sortField,
  enabled = true,
  limit = 20,
  sortOrder = "DESC",
  enrichWithMarket = false,
  enrichWithSymbol = false,
  enrichWithSymbolLimit = 5,
  enrichEnable = false,
  stockType,
  scope,
}: UseThemesInfiniteQueryParams) => {
  const openDataApi = getOpenDataApi();

  // 前端 locale，后续在构建查询参数时统一转换为后端 language
  const locale = I18nUtil.getCurrentLocale() as Locale;

  // 构建查询配置（统一管理所有查询参数）
  const queryConfig: Omit<ThemesQueryConfig, "offset"> = {
    limit,
    type,
    sortField,
    sortOrder,
    enrichWithMarket,
    enrichWithSymbol,
    enrichWithSymbolLimit,
    enrichEnable,
    stockType,
    scope,
    locale,
  };

  const queryKey = buildThemesQueryKey(queryConfig);

  return useInfiniteQuery<ThemesPagination>({
    // 构建完整的 queryKey（包含所有影响结果的参数）
    queryKey,
    queryFn: async ({ pageParam }) => {
      log.debug(
        `[useThemesInfiniteQuery] 获取主题数据:`,
        JSON.stringify(pageParam),
      );

      const params = pageParam as GetRankingThemesParams;
      const response = await openDataApi?.themes.getRankingThemes(params);

      const paginationData = response?.data?.data;

      if (!paginationData || !Array.isArray(paginationData.rows)) {
        log.warn(`[useThemesInfiniteQuery] 返回数据为空或格式错误`);
        return {
          rows: [],
          total: 0,
          offset: params.offset,
          limit: params.limit,
        };
      }

      return {
        rows: paginationData.rows,
        total: paginationData.total || 0,
        offset: params.offset,
        limit: params.limit,
      };
    },
    // 使用统一的参数构建函数
    initialPageParam: buildThemesQueryParams({ ...queryConfig, offset: 0 }),
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.limit;
      // 如果已经获取了所有数据，返回 undefined
      if (nextOffset >= lastPage.total) {
        return undefined;
      }
      // 使用统一的参数构建函数
      return buildThemesQueryParams({ ...queryConfig, offset: nextOffset });
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5分钟
  });
};
